import * as React from 'react';
import { scan, warnOnce } from '../../utils/common.js';

const isNavigationHierarchyItem = (item) => {
  if (typeof item !== "object" || item === null) {
    return false;
  }
  if (!("item" in item)) {
    return false;
  }
  if (!("path" in item)) {
    return false;
  }
  if ("items" in item) {
    return false;
  }
  return true;
};
const isNavigationHierarchySubmenu = (item) => {
  if (typeof item !== "object" || item === null) {
    return false;
  }
  if (!("title" in item)) {
    return false;
  }
  if (!("path" in item)) {
    return false;
  }
  if (!("items" in item)) {
    return false;
  }
  return true;
};
const parsePath = (path, root, submenuPath) => {
  if (submenuPath === void 0) {
    if (path === root) {
      return "/";
    } else if (!path.startsWith("/")) {
      return `/${path}`;
    } else {
      return path;
    }
  }
  if (path === root) {
    return submenuPath;
  } else if (!path.startsWith("/")) {
    if (submenuPath === "/") {
      return `/${path}`;
    }
    return `${submenuPath}/${path}`;
  } else {
    return path;
  }
};
const parseHierarchy = (hierarchy, root) => {
  return hierarchy.map((entry) => {
    if (isNavigationHierarchyItem(entry)) {
      const { path, item, icon } = entry;
      return {
        key: parsePath(path, root),
        item,
        icon
      };
    }
    if (isNavigationHierarchySubmenu(entry)) {
      const { path: submenuPath, items, ...rest } = entry;
      let parsedSubmenuPath = parsePath(submenuPath, root);
      return {
        ...rest,
        key: parsedSubmenuPath,
        items: items.map((child) => {
          if (!isNavigationHierarchyItem(child)) {
            throw new Error(`Navigation submenu item ${JSON.stringify(child)} should be of type NavigationHierarchyItem.`);
          }
          const { path, item, ...rest2 } = child;
          return {
            ...rest2,
            key: parsePath(path, root, parsedSubmenuPath),
            title: item
          };
        })
      };
    }
    throw new Error(`Navigation hierarchy object ${JSON.stringify(entry)} should be of type NavigationHierarchyItem or NavigationHierarchySubmenu.`);
  });
};
const parseRouteHierarchy = ({ hierarchy, root }) => {
  return Object.entries(hierarchy).map(([key, entry]) => {
    const { title, item, items } = entry;
    let itemKeyPrefix;
    if (key === root) {
      key = "/";
      itemKeyPrefix = "";
    } else {
      itemKeyPrefix = key = key.startsWith("/") ? key : `/${key}`;
    }
    if (title) {
      if (!items) {
        throw new Error(`Navigation hierarchy object '${key}' should define items, as it has a 'title' property.
If you want to define a simple item instead of a submenu, define the property 'item' instead of 'title'.`);
      }
      return {
        key,
        title,
        items: Object.entries(items).map(([itemKey, itemContent]) => {
          if (!React.isValidElement(itemContent) && typeof itemContent == "object") {
            throw new Error(`SimpleNavigation${"\n"}Navigation submenu child object with key '${itemKey}' has a invalid value. Pass a valid ReactNode instead.`);
          }
          return {
            key: `${itemKeyPrefix}/${itemKey}`,
            title: itemContent
          };
        })
      };
    } else if (items) {
      throw new Error(`Navigation hierarchy with item ${JSON.stringify(item)} should not have items as children.
If you want to define a sub menu, define the properties 'title' and 'items' instead of an 'item' property.`);
    } else {
      return { key, item };
    }
  });
};
const normalizeLocation = ({ location, root }, subMenuLookup) => {
  if (!location.startsWith("/")) {
    throw new Error(`SimpleNavigation location property should start with '/' but got ${location}`);
  }
  const normalizedLocation = location.split(/[?#]/)[0].split("/").splice(1).map(scan((acc, next) => `${acc}/${next}`, "")).reverse().filter((str) => str in subMenuLookup)[0];
  if (normalizedLocation) {
    return normalizedLocation;
  } else if (root) {
    return "/";
  } else {
    warnOnce(`SimpleNavigation${"\n"}No valid location could be determined for location: ${location}`);
    return void 0;
  }
};
const createSubMenuLookup = (parsedHierarchy) => {
  const lookup = {};
  for (const { key, items } of parsedHierarchy) {
    lookup[key] = key;
    if (items) {
      for (const next of items) {
        lookup[next.key] = key;
      }
    }
  }
  return lookup;
};

export { createSubMenuLookup, isNavigationHierarchyItem, isNavigationHierarchySubmenu, normalizeLocation, parseHierarchy, parsePath, parseRouteHierarchy };
